// Tiny markdown-to-React renderer (no dependency)
// Supports: H2, H3, paragraphs, bold, links, ul, ol, tables, blockquote, hr
import React from "react";

const inline = (text) => {
  // [link text](url)
  let out = text.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" class="text-gold underline underline-offset-2 hover:text-gold-500 transition-colors">$1</a>'
  );
  // **bold**
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // *italic*
  out = out.replace(/(^|[^*])\*([^*]+)\*([^*]|$)/g, "$1<em>$2</em>$3");
  return out;
};

export const renderMarkdown = (md) => {
  const lines = md.split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Empty line
    if (line.trim() === "") {
      i++;
      continue;
    }

    // H2
    if (line.startsWith("## ")) {
      blocks.push(
        <h2
          key={i}
          className="font-serif text-3xl md:text-4xl text-ink mt-14 mb-6 leading-tight tracking-tight"
        >
          {line.slice(3)}
        </h2>
      );
      i++;
      continue;
    }

    // H3
    if (line.startsWith("### ")) {
      blocks.push(
        <h3
          key={i}
          className="font-serif text-2xl text-ink mt-10 mb-4 leading-tight"
        >
          {line.slice(4)}
        </h3>
      );
      i++;
      continue;
    }

    // HR
    if (/^---+$/.test(line.trim())) {
      blocks.push(<hr key={i} className="my-10 border-gold/20" />);
      i++;
      continue;
    }

    // Table (markdown style)
    if (line.startsWith("| ")) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      // Skip separator line (---|---|---)
      const headerCells = tableLines[0]
        .split("|")
        .filter((c) => c.trim())
        .map((c) => c.trim());
      const rows = tableLines.slice(2).map((row) =>
        row
          .split("|")
          .filter((c) => c.trim())
          .map((c) => c.trim())
      );
      blocks.push(
        <div key={i} className="my-8 overflow-x-auto rounded-2xl border border-gold/20">
          <table className="w-full text-sm font-sans">
            <thead className="bg-cream">
              <tr>
                {headerCells.map((h, k) => (
                  <th
                    key={k}
                    className="text-left px-5 py-3 font-semibold text-ink border-b border-gold/20"
                    dangerouslySetInnerHTML={{ __html: inline(h) }}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, rk) => (
                <tr key={rk} className="even:bg-cream/40">
                  {r.map((c, ck) => (
                    <td
                      key={ck}
                      className="px-5 py-3 text-ink/85 border-b border-ink/5"
                      dangerouslySetInnerHTML={{ __html: inline(c) }}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Unordered list
    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].slice(2));
        i++;
      }
      blocks.push(
        <ul key={i} className="my-6 space-y-2 font-sans text-base text-ink/80 leading-relaxed list-none">
          {items.map((it, k) => (
            <li key={k} className="flex gap-3">
              <span className="text-gold font-bold flex-shrink-0">→</span>
              <span dangerouslySetInnerHTML={{ __html: inline(it) }} />
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ""));
        i++;
      }
      blocks.push(
        <ol key={i} className="my-6 space-y-3 font-sans text-base text-ink/80 leading-relaxed list-decimal pl-6 marker:text-gold marker:font-bold">
          {items.map((it, k) => (
            <li key={k} dangerouslySetInnerHTML={{ __html: inline(it) }} />
          ))}
        </ol>
      );
      continue;
    }

    // Paragraph (default)
    blocks.push(
      <p
        key={i}
        className="my-5 font-sans text-base md:text-lg text-ink/80 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: inline(line) }}
      />
    );
    i++;
  }

  return blocks;
};
